import { useEffect, useState } from "react";
import { api, STATUS_COLORS } from "../api";
import { useLang, fmtRM, fmtDate } from "../i18n";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Flag, Check, X, Umbrella, ReceiptText } from "lucide-react";
import { toast } from "sonner";

export default function Approvals() {
  const { lang } = useLang();
  const bm = lang === "bm";
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");

  const load = () => api.get("/approvals").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const decide = async (item, action) => {
    const url = item.kind === "LEAVE" ? `/leave/requests/${item.request_id}/decision` : `/claims/${item.claim_id}/decision`;
    try {
      await api.post(url, { action });
      toast.success(action === "approve" ? (bm ? "Diluluskan" : "Approved") : (bm ? "Ditolak" : "Rejected"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const filtered = items.filter((i) => tab === "all" || i.kind === tab.toUpperCase());

  return (
    <div data-testid="approvals-page" className="space-y-6">
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{bm ? "Kelulusan" : "Approvals"}</h1>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger data-testid="approvals-tab-all" value="all">{bm ? "Semua" : "All"} ({items.length})</TabsTrigger>
          <TabsTrigger data-testid="approvals-tab-leave" value="leave">{bm ? "Cuti" : "Leave"}</TabsTrigger>
          <TabsTrigger data-testid="approvals-tab-claim" value="claim">{bm ? "Tuntutan" : "Claims"}</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="space-y-3">
        {filtered.map((item) => (
          <div key={item.request_id || item.claim_id} data-testid={`approval-item-${item.request_id || item.claim_id}`}
            className="bg-card border rounded-xl p-4 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${item.kind === "LEAVE" ? "bg-blue-50 text-blue-700" : "bg-emerald-50 text-emerald-700"}`}>
                {item.kind === "LEAVE" ? <Umbrella className="w-4 h-4" /> : <ReceiptText className="w-4 h-4" />}
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-sm">{item.employee_name}</p>
                {item.kind === "LEAVE" ? (
                  <p className="text-sm text-slate-600">{item.leave_type} · {fmtDate(item.start_date)} → {fmtDate(item.end_date)} · {item.days} {bm ? "hari" : "days"}</p>
                ) : (
                  <p className="text-sm text-slate-600">{item.type} · {fmtRM(item.amount)} · {fmtDate(item.claim_date)}</p>
                )}
                {item.reason && <p className="text-xs text-slate-500 mt-0.5">“{item.reason}”</p>}
                {item.description && <p className="text-xs text-slate-500 mt-0.5">“{item.description}”</p>}
                {item.flags?.map((f, i) => (
                  <p key={i} className="text-xs text-amber-700 flex items-center gap-1 mt-0.5"><Flag className="w-3 h-3" /> {f}</p>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={STATUS_COLORS[item.status] || ""}>{item.status}</Badge>
              <Button data-testid={`approve-${item.request_id || item.claim_id}`} size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => decide(item, "approve")}>
                <Check className="w-4 h-4 mr-1" /> {bm ? "Lulus" : "Approve"}
              </Button>
              <Button data-testid={`reject-${item.request_id || item.claim_id}`} size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => decide(item, "reject")}>
                <X className="w-4 h-4 mr-1" /> {bm ? "Tolak" : "Reject"}
              </Button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="bg-card border rounded-xl p-10 text-center text-sm text-slate-500">
            {bm ? "Tiada permohonan tertangguh. Semua selesai!" : "No pending requests. All clear!"}
          </div>
        )}
      </div>
    </div>
  );
}
